import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "react-bootstrap";
import Swal from 'sweetalert2';
import { ArrowRight, ArrowLeft, RocketTakeoffFill, FloppyFill } from "react-bootstrap-icons";

import RateAgreementStep from "../components/RateAgreementStep";
import RateConceptsStep from "../components/RateConceptsStep";
import RateConditionsStep from "../components/RateConditionsStep";
import RateItemsStep from "../components/RateItemsStep";
import RateReviewStep from "../components/RateReviewStep";


import { validateRateAgreement } from "../api/validations";
import api from "../api/axios";

import "../styles/rateWizard.css";

const STEPS = [
  { id: "rate_agreement", title: "Acuerdo Tarifario" },
  { id: "concepts", title: "Conceptos tarifarios" },
  { id: "conditions", title: "Condiciones" },
  { id: "items", title: "Ítems y reglas" },
  { id: "review", title: "Revisión y publicación" },
];

const toDateInput = (value) => {
  if (!value) return "";
  return value.split("T")[0]; // YYYY-MM-DD
};


export default function RateWizard() {
  const navigate = useNavigate();
  const { rateId } = useParams();

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [loading, setLoading] = useState(Boolean(rateId));
  const [allConditions, setAllConditions] = useState([]);
  const [places, setPlaces] = useState([]);
  const [wizardStepFromBackend, setWizardStepFromBackend] = useState(null);
  const [isSavingSig, setIsSavingSig] = useState(false);
  const [isSavingBor, setIsSavingBor] = useState(false);

  const [formData, setFormData] = useState({
    rate: {
      name: "",
      number: "",
      year: "",
      issued_at: "",
      effective_from: "",
      effective_to: "",
      no_end_date: false,
      document_url: "",
    },
    concepts: [],
    conditions: [], 
    items: [], 
  });

  const step = STEPS[currentStepIndex];


  const goToReview = () => {
    if (hasDirtyChanges) {
      Swal.fire({
          title: 'Error',
          text: "Tiene cambios sin guardar.\n\n" +
                "Guarde el borrador o avance con 'Siguiente' antes de ir a revisión.",
          icon: 'error',
          timer: 1500,
          showConfirmButton: true
      });
      return;
    }

    setCurrentStepIndex(4);
  };

  const [dirtySteps, setDirtySteps] = useState({
    agreement: false,
    concepts: false,
    conditions: false,
    items: false,
  });


  /* ===============================
     Cargar rate SOLO si existe
     =============================== */
  useEffect(() => {
    if (!rateId) return;

    const loadRate = async () => {
      try {
        const res = await api.get(`/rates/${rateId}`);
        const rate = res.data.data;
        setWizardStepFromBackend(rate.wizard_step);

        setFormData({
          rate: {
            name: rate.name,
            number: rate.number,
            year: rate.year,
            issued_at: toDateInput(rate.issued_at),
            effective_from: toDateInput(rate.effective_from),
            effective_to: toDateInput(rate.effective_to),
            no_end_date: !rate.effective_to,
            document_url: rate.document_url ?? "",
          },
          concepts: rate.concepts ?? [],
          conditions: rate.conditions ?? [],
          items: rate.items ?? [],  
        });

        if (rate.wizard_step > 1) {
          setCurrentStepIndex(rate.wizard_step - 1);
        }
      } catch (err) {
        Swal.fire({
          title: 'Error',
          text: "No fue posible cargar el acuerdo.",
          icon: 'error',
          timer: 1500,
          showConfirmButton: false
        });
      } finally {
        setLoading(false);
      }
    };

    loadRate();
  }, [rateId]);

  const reloadRateFromBackend = async () => {
    const res = await api.get(`/rates/${rateId}`);
    const rate = res.data.data;

    setFormData({
      rate: {
        name: rate.name,
        number: rate.number,
        year: rate.year,
        issued_at: toDateInput(rate.issued_at),
        effective_from: toDateInput(rate.effective_from),
        effective_to: toDateInput(rate.effective_to),
        no_end_date: !rate.effective_to,
        document_url: rate.document_url ?? "",
      },
      concepts: rate.concepts ?? [],
      conditions: rate.conditions ?? [],
      items: rate.items ?? [],
    });

    setWizardStepFromBackend(rate.wizard_step);
  };

  const hasErrorsItems = useMemo(
    () =>
      !formData.items ||
      formData.items.length === 0 ||
      formData.items.some(
        (i) => !i.value_cents || i.value_cents <= 0
      ),
    [formData.items]
  );

  const hasErrorsConcepts = useMemo(
    () => !formData.concepts || formData.concepts.length === 0,
    [formData.concepts]
  );

  const hasErrorsConditions = useMemo(
    () => !formData.conditions || formData.conditions.length === 0,
    [formData.conditions]
  );

  useEffect(() => {
    const loadConditionsCatalog = async () => {
      try {
        const res = await api.get("/conditions");
        setAllConditions(res.data.data ?? []);
      } catch (err) {
        console.error("Error cargando condiciones:", err);
      }
    };

    loadConditionsCatalog();
  }, []);

  useEffect(() => {
    const loadPlacesCatalog = async () => {
      try {
        const plac = await api.get("/places");

        setPlaces(plac.data.data ?? []);
      } catch (err) {
        console.error("Error cargando catálogos:", err);
      }
    };

    loadPlacesCatalog();
  }, []);


    /* ===============================
     Validación Paso 1
     =============================== */
  const stepValidation = useMemo(() => {
    if (step.id === "rate_agreement") {
      return validateRateAgreement(formData.rate);
    }
    return { isValid: false, errors: {} };
  }, [step.id, formData.rate]);

    /* ===============================
     Paso 1 → CREAR rate
     =============================== */
  const goNext = async () => {
    if (!stepValidation.isValid) return;

    setIsSavingSig(true);

    // ✅ SI YA EXISTE rateId → SOLO AVANZA
    if (rateId) {
      await api.post(`/rates/${rateId}`, {
        wizard_step: 2,
        document_url: formData.rate.document_url,
      });

      setCurrentStepIndex(1);
      setIsSavingSig(false);
      return;
    }

    // 🆕 SOLO SI NO EXISTE → CREAR RATE
    try {
      const res = await api.post("/rates", {
        ...formData.rate,
        effective_to: formData.rate.no_end_date
          ? null
          : formData.rate.effective_to,
      });

      const created = res.data.data;

      navigate(`/rates/${created.id}/wizard`, { replace: true });
      setCurrentStepIndex(1);
    } catch (err) {
      if (err.response?.status === 422) {
        Swal.fire({
          title: 'Error',
          text: "Revise los campos obligatorios.",
          icon: 'error',
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        Swal.fire({
          title: 'Error',
          text: "No fue posible guardar el acuerdo.",
          icon: 'error',
          timer: 1500,
          showConfirmButton: false
        });
      }
    } finally {
      setIsSavingSig(false);
    }
  };

  const persistStep2Concepts = async () => {
    if (!rateId) return;

    await api.post(`/rates/${rateId}/concepts`, {
      concept_ids: formData.concepts.map(c => c.id),
    });

    setDirtySteps(prev => ({ ...prev, concepts: false }));
    await reloadRateFromBackend();
  };

  const persistStep3Conditions = async () => {
    if (!rateId) return;

    await api.post(`/rates/${rateId}/conditions`, {
      conditions: formData.conditions,
    });

    setDirtySteps(prev => ({ ...prev, conditions: false }));
    await reloadRateFromBackend();
  };

  const persistStep4Items = async () => {
    if (!rateId) return;

    await api.post(`/rates/${rateId}/items`, {
      items: formData.items.map(i => ({
        id: i.id,
        condition_group_id: i.condition_group_id ?? null,
        value_cents: i.value_cents,
      })),
    });

    setDirtySteps(prev => ({ ...prev, items: false }));
    await reloadRateFromBackend();
  };


  /* ===============================
   Guardar borrador (PASOS ≥ 2)
   =============================== */
  const saveDraft = async () => {
    if (!rateId) return;

    setIsSavingBor(true);

    try {
      
      if (step.id === "concepts") {
        await persistStep2Concepts();
      }

      if (step.id === "conditions") {
        await persistStep3Conditions();
      }

      if (step.id === "items") {
        await persistStep4Items();
      }

      await api.post(`/rates/${rateId}`, {
        wizard_step: currentStepIndex + 1,
      });

      Swal.fire({
        title: 'Éxito',
        text: "Borrador guardado correctamente.",
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (err) {
      console.error("Error guardando borrador:", err);
      Swal.fire({
        title: 'Error',
        text: "No fue posible guardar el borrador.",
        icon: 'error',
        timer: 1500,
        showConfirmButton: false
      });
    } finally {
      setIsSavingBor(false);
    }
  };

  /* ===============================
   Publicar tarifa (PASO 5)
   =============================== */
  const publishRate = async () => {

    if (!rateId) return;

    setIsSavingSig(true);

    try {
      await api.post(`/rates/${rateId}/publish`);

      Swal.fire({
        title: 'Éxito',
        text: "Tarifa publicada correctamente.",
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      });

      navigate("/rates");
    } catch (err) {
      console.error("Error publicando tarifa:", err);

      if (err.response?.status === 422) {
        Swal.fire({
          title: 'Error',
          text: "La tarifa no cumple las validaciones finales.",
          icon: 'error',
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        Swal.fire({
          title: 'Error',
          text: "No fue posible publicar la tarifa.",
          icon: 'error',
          timer: 1500,
          showConfirmButton: false
        });
      }
    } finally {
      setIsSavingSig(false);
    }
  };

  const catalogConditions = useMemo(
    () => allConditions.filter(c => c.rate_id === null),
    [allConditions]
  );

  const handleBack = async () => {
    const result = await Swal.fire({
      title: "¿Desea salir?",
      text: "Si no ha guardado los cambios, estos se perderán.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, salir",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#0d6efd",   // Bootstrap primary
      cancelButtonColor: "#6c757d",    // Bootstrap secondary
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    navigate("/rates");
  };

  if (loading) return <div className="text-center p-3" style={{marginTop: '10%'}}>
                        <div className="spinner-border text-primary"></div>
                        <p>Cargando...</p>
                    </div>;

  const progress = Math.round(((currentStepIndex + 1) / STEPS.length) * 100);


  const hasDirtyChanges = Object.values(dirtySteps).some(Boolean);
  {hasDirtyChanges && (
    <div className="alert alert-warning py-1 mb-2">
      Tiene cambios sin guardar
    </div>
  )}

  return (
    <div className="wizard-page">
      <header className="wizard-header d-flex justify-content-between align-items-center">
        <div>
          <strong>
            Paso {currentStepIndex + 1} de {STEPS.length}
          </strong>{" "}
          — {step.title}

          <div className="wizard-progress mt-2">
            <div
              className="wizard-progress-bar"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <Button
          variant="outline-secondary"
          size="sm"
          onClick={handleBack}
        >
          ← Volver a tarifas
        </Button>

        {wizardStepFromBackend === 5 && currentStepIndex !== 4 && (
          <Button
            variant="outline-primary"
            size="sm"
            onClick={goToReview}
          >
            Ir a revisión
          </Button>
        )}

      </header>


      <main className="wizard-content">
        {step.id === "rate_agreement" && (
          <RateAgreementStep
            value={formData.rate}
            errors={stepValidation.errors}
            onChange={(patch) =>
              setFormData((prev) => ({
                ...prev,
                rate: { ...prev.rate, ...patch },
              }))
            }
          />
        )}
        {step.id === "concepts" && (
          <RateConceptsStep
            selectedConcepts={formData.concepts ?? []}
            items={formData.items ?? []}
            onChange={(concepts) => {
              setFormData((prev) => ({ ...prev, concepts }));
              setDirtySteps(prev => ({ ...prev, concepts: true }));
            }}
          />
        )}
        {step.id === "conditions" && (
          <RateConditionsStep
            catalogConditions={catalogConditions}
            selectedConditions={formData.conditions}
            rateConcepts={formData.concepts}
            places={places}
            onChange={(conditions) => {
              setFormData(prev => ({ ...prev, conditions }));
              setDirtySteps(prev => ({ ...prev, conditions: true }));
            }}
          />
        )}

        {step.id === "items" && (
          <RateItemsStep
            concepts={formData.concepts}
            conditions={formData.conditions}
            items={formData.items}
            catalogConditions={catalogConditions}
            places={places}
            onChange={(items) => {
              setFormData((prev) => ({ ...prev, items }));
              setDirtySteps(prev => ({ ...prev, items: true }));
            }
            }
          />
        )}

        {step.id === "review" && (
          <RateReviewStep
            rate={formData.rate}
            concepts={formData.concepts}
            conditions={formData.conditions}
            items={formData.items}
            places={places}
            hasErrorsConcepts={hasErrorsConcepts}
            hasErrorsConditions={hasErrorsConditions}
            hasErrorsItems={hasErrorsItems}
            onEditStep={(index) => setCurrentStepIndex(index)}
            onPublish={publishRate}
          />
        )}

        {!["rate_agreement", "concepts", "conditions", "items", "review"].includes(step.id) && (
          <div className="wizard-placeholder">
            Paso pendiente de implementar
          </div>
        )}

      </main>

      <footer className="wizard-footer">

        {/* ===============================
            PASO 1 — Crear acuerdo
          =============================== */}
        {step.id === "rate_agreement" && (
          <button
            onClick={goNext}
            disabled={!stepValidation.isValid || isSavingSig}
            className="btn-primary"
          >
            {isSavingSig ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Guardando...
                </>
              ) : (
                <>
                  Siguiente <ArrowRight />
                </>
              )}
          </button>
        )}

        {/* ===============================
            PASOS ≥ 2 — Wizard normal
          =============================== */}
        {step.id !== "rate_agreement" && step.id !== "review" && (
          <>
            {/* Volver */}
            <button
              className="btn-secondary"
              disabled={
                isSavingBor || 
                isSavingSig}
              onClick={() =>
                setCurrentStepIndex((i) => Math.max(i - 1, 0))
              }
            >
              <ArrowLeft /> Volver
            </button>

            {/* Guardar borrador */}
            <button
              className="btn-draft"
              disabled={
                (step.id === "concepts" &&
                  (!formData.concepts || formData.concepts.length === 0)) ||

                (step.id === "conditions" &&
                  (!formData.conditions || formData.conditions.length === 0)) ||

                (step.id === "items" &&
                  (!formData.items ||
                    formData.items.length === 0 ||
                    formData.items.some(i => !i.value_cents || i.value_cents <= 0))) ||

                isSavingSig || isSavingBor
              }
              onClick={saveDraft}
            >
              {isSavingBor ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Guardando...
                </>
              ) : (
                <>
                  Guardar borrador <FloppyFill />
                </>
              )}
            </button>

            {/* Siguiente */}
            <button
              className="btn-primary"
              disabled={
                (step.id === "concepts" &&
                  (!formData.concepts || formData.concepts.length === 0)) ||

                (step.id === "conditions" &&
                  (!formData.conditions || formData.conditions.length === 0)) ||

                (step.id === "items" &&
                  (!formData.items ||
                    formData.items.length === 0 ||
                    formData.items.some(i => !i.value_cents || i.value_cents <= 0))) ||

                isSavingSig 
                || isSavingBor
              }

              onClick={async () => {
                try {
                  setIsSavingSig(true);

                  if (step.id === "concepts") {
                    await persistStep2Concepts();
                  }

                  if (step.id === "conditions") {
                    await persistStep3Conditions();
                  }

                  if (step.id === "items") {
                    await persistStep4Items();
                  }

                  setCurrentStepIndex((i) =>
                    Math.min(i + 1, STEPS.length - 1)
                  );
                } catch (err) {
                  Swal.fire({
                    title: 'Error',
                    text: "No fue posible guardar los conceptos.",
                    icon: 'error',
                    timer: 1500,
                    showConfirmButton: false
                  });
                } finally {
                  setIsSavingSig(false);
                }
              }}
            >
              {isSavingSig ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Guardando...
                </>
              ) : (
                <>
                  Siguiente <ArrowRight />
                </>
              )}
              
            </button>
          </>
        )}
        {step.id === "review" && !hasDirtyChanges && (
          <>
            <button
              className="btn-secondary" 
              disabled={
                isSavingBor || 
                isSavingSig}
              onClick={() =>
                setCurrentStepIndex((i) => Math.max(i - 1, 0))
              }
            >
              <ArrowLeft /> Volver
            </button>

            <button
              disabled={hasErrorsConcepts || hasErrorsItems || isSavingSig}
              className="btn-primary"
              onClick={publishRate}
            >
              {isSavingSig ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Publicando...
                </>
              ) : (
                <>
                  Publicar tarifa <RocketTakeoffFill />
                </>
              )}
            </button>
          </>
        )}
      </footer>
    </div>
  );
}
